import { Link } from "@/lib/link";
import { useTranslation } from "react-i18next";
import { ArrowRight, ShieldCheck, Ship, Sparkles, Factory, PackageCheck, ClipboardCheck, Handshake, Globe2, Play } from "lucide-react";
import SEO from "@/components/common/SEO";
import ScrollReveal from "@/components/common/ScrollReveal";
import StatCounter from "@/components/common/StatCounter";
import SectionHeading from "@/components/common/SectionHeading";
import WorldMap from "@/components/common/WorldMap";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { productCategories } from "@/data/products";
import { industries } from "@/data/industries";
import { faqs } from "@/data/faqs";
import { HERO } from "@/constants/testIds";

const heroBg = "https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&w=1800&q=80";

const testimonials = [
    { quote: "Exportfy became our default sourcing partner for hotel bath linens across three properties. Consistent, on-time and on-spec.", author: "Procurement Director", company: "5-star hotel chain · Mauritius" },
    { quote: "We piloted 1 container of coveralls; today Exportfy runs our entire India workwear program.", author: "Head of Sourcing", company: "Distributor · Oman" },
    { quote: "Their factory audits saved us from a bad supplier. Rare to find that discipline in merchant exporters.", author: "Buying House Owner", company: "Singapore" },
];

const homeFAQs = faqs.slice(0, 6);
const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: homeFAQs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
};

export default function Home() {
    const { t } = useTranslation();
    return (
        <>
            <SEO
                title="Exportfy.world — Global Merchant Exporter & Trade Enablement Platform"
                description="Exportfy.world is a 5+ year old global merchant exporter shipping to Mauritius, Brunei, Malaysia, Singapore, and Oman. OEM, private label, custom sourcing across textiles, safety, and agro."
                path="/"
                schema={faqSchema}
            />

            {/* HERO */}
            <section className="relative overflow-hidden bg-[#0B2F28] grain-overlay">
                <div className="absolute inset-0">
                    <img src={heroBg} alt="Global cargo shipping" className="w-full h-full object-cover opacity-40" />
                    <div className="absolute inset-0 bg-gradient-to-br from-[#0B2F28] via-[#123D35]/85 to-[#0B2F28]" />
                </div>
                <div className="relative container-x pt-40 pb-24 sm:pt-48 sm:pb-28">
                    <div className="max-w-4xl">
                        <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-white/80">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                            </span>
                            {t("hero.badge")}
                        </div>
                        <h1 className="mt-6 text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight text-white leading-[1.02]">
                            {t("hero.titleLine1")}
                            <span className="block text-white/85">{t("hero.titleLine2")}</span>
                            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-emerald-200">{t("hero.titleLine3")}</span>
                        </h1>
                        <p className="mt-7 text-lg sm:text-xl text-white/70 leading-relaxed max-w-3xl">
                            {t("hero.subtitle")}
                        </p>

                        <div className="mt-9 flex flex-wrap gap-3">
                            <Link to="/consultation" data-testid={HERO.consultationCta}>
                                <Button className="rounded-full bg-white text-[#0A2540] hover:bg-slate-100 font-semibold px-7 py-6 text-base">
                                    {t("hero.cta1")} <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                            </Link>
                            <Link to="/custom-sourcing" data-testid={HERO.sourcingCta}>
                                <Button className="rounded-full bg-[#059669] hover:bg-[#047857] text-white font-semibold px-7 py-6 text-base">
                                    {t("hero.cta2")}
                                </Button>
                            </Link>
                            <Link to="/global-partner" data-testid={HERO.supplierCta}>
                                <Button variant="outline" className="rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white font-semibold px-7 py-6 text-base">
                                    {t("hero.cta3")}
                                </Button>
                            </Link>
                        </div>

                        <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-8 max-w-3xl">
                            {[
                                { v: "5+", l: t("hero.stat1") },
                                { v: "5", l: t("hero.stat2") },
                                { v: "17+", l: t("hero.stat3") },
                                { v: "6", l: t("hero.stat4") },
                            ].map((s) => (
                                <div key={s.l}>
                                    <div className="text-3xl sm:text-4xl font-extrabold text-white">{s.v}</div>
                                    <div className="mt-1 text-xs uppercase tracking-[0.18em] text-white/50">{s.l}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* STATS + MAP */}
            <section className="bg-[#103C34] text-white section-y">
                <div className="container-x">
                    <ScrollReveal>
                        <SectionHeading
                            light
                            eyebrow={t("footprint.eyebrow")}
                            title={t("footprint.title")}
                            subtitle={t("footprint.subtitle")}
                        />
                    </ScrollReveal>

                    <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
                        <div className="space-y-10 lg:space-y-14">
                            <StatCounter end={5} suffix="+" label={t("footprint.yearsActive")} />
                            <StatCounter end={22} suffix="+" label={t("footprint.totalMarkets")} />
                            <StatCounter end={6} label={t("footprint.verticals")} />
                        </div>
                        <div className="lg:col-span-2">
                            <ScrollReveal delay={80}>
                                <WorldMap />
                            </ScrollReveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* PRODUCT CATEGORIES */}
            <section id="products" className="section-y">
                <div className="container-x">
                    <ScrollReveal>
                        <SectionHeading
                            eyebrow={t("categories.eyebrow")}
                            title={t("categories.title")}
                            subtitle={t("categories.subtitle")}
                        />
                    </ScrollReveal>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {productCategories.map((c, i) => (
                            <ScrollReveal key={c.slug} delay={i * 60}>
                                <Link
                                    to={`/products/${c.slug}`}
                                    data-testid={`product-card-${c.slug}`}
                                    className="group relative block bg-white rounded-2xl overflow-hidden border border-slate-200 transition-all duration-500 hover:shadow-[0_20px_40px_rgba(10,37,64,0.10)] hover:-translate-y-1"
                                >
                                    <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                                        <img src={c.image} alt={t(c.nameKey)} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-xl font-bold text-[#0A2540]">{t(c.nameKey)}</h3>
                                            <ArrowRight className="w-4 h-4 text-[#1D4ED8] transition-transform group-hover:translate-x-1" />
                                        </div>
                                        <p className="mt-2 text-sm text-slate-500 leading-relaxed">{t(c.taglineKey)}</p>
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            {c.oem && <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-1">{t("products.badges.oem")}</span>}
                                            {c.privateLabel && <span className="text-[10px] font-semibold uppercase tracking-widest text-amber-700 bg-amber-50 rounded-full px-2.5 py-1">{t("products.badges.privateLabel")}</span>}
                                        </div>
                                    </div>
                                </Link>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* INDUSTRIES */}
            <section className="section-y bg-[#F4FBF8]">
                <div className="container-x">
                    <ScrollReveal>
                        <SectionHeading
                            eyebrow={t("industries.eyebrow")}
                            title={t("industries.title")}
                            subtitle={t("industries.subtitle")}
                        />
                    </ScrollReveal>
                    <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {industries.map((ind, i) => (
                            <ScrollReveal key={ind.slug} delay={i * 40}>
                                <Link to="/industries" data-testid={`industry-card-${ind.slug}`} className="group relative block rounded-2xl overflow-hidden aspect-[4/5]">
                                    <img src={ind.image} alt={ind.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#061A2E] via-[#061A2E]/40 to-transparent" />
                                    <div className="absolute bottom-0 p-5">
                                        <div className="text-white font-bold text-lg">{ind.name}</div>
                                        <div className="text-white/70 text-xs mt-1 line-clamp-2">{ind.summary}</div>
                                    </div>
                                </Link>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY EXPORTFY */}
            <section className="section-y">
                <div className="container-x">
                    <ScrollReveal>
                        <SectionHeading
                            eyebrow={t("why.eyebrow")}
                            title={t("why.title")}
                            subtitle={t("why.subtitle")}
                        />
                    </ScrollReveal>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            { Icon: ShieldCheck, title: t("home.why.items.accountability.title"), desc: t("home.why.items.accountability.description") },
                            { Icon: Factory, title: t("home.why.items.factoryNetwork.title"), desc: t("home.why.items.factoryNetwork.description") },
                            { Icon: Ship, title: t("home.why.items.freight.title"), desc: t("home.why.items.freight.description") },
                            { Icon: Sparkles, title: t("home.why.items.oem.title"), desc: t("home.why.items.oem.description") },
                            { Icon: PackageCheck, title: t("home.why.items.discipline.title"), desc: t("home.why.items.discipline.description") },
                            { Icon: Handshake, title: t("home.why.items.partnerships.title"), desc: t("home.why.items.partnerships.description") },
                        ].map(({ Icon, title, desc }, i) => (
                            <ScrollReveal key={t} delay={i * 50}>
                                <div className="bg-white border border-slate-200 rounded-2xl p-7 h-full transition-all hover:shadow-[0_20px_40px_rgba(10,37,64,0.08)] hover:-translate-y-0.5">
                                    <div className="w-12 h-12 rounded-xl bg-[#0A2540] flex items-center justify-center text-white">
                                        <Icon className="w-5 h-5" strokeWidth={1.75} />
                                    </div>
                                    <h3 className="mt-5 font-bold text-lg text-[#0A2540]">{title}</h3>
                                    <p className="mt-2 text-sm text-slate-500 leading-relaxed">{desc}</p>
                                </div>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="section-y bg-[#103C34] text-white">
                <div className="container-x">
                    <ScrollReveal>
                        <SectionHeading light eyebrow={t("how.eyebrow")} title={t("how.title")} subtitle={t("how.subtitle")} />
                    </ScrollReveal>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { n: "01", title: t("home.how.steps.discovery.title"), desc: t("home.how.steps.discovery.description") },
                            { n: "02", title: t("home.how.steps.sourcing.title"), desc: t("home.how.steps.sourcing.description") },
                            { n: "03", title: t("home.how.steps.production.title"), desc: t("home.how.steps.production.description") },
                            { n: "04", title: t("home.how.steps.delivery.title"), desc: t("home.how.steps.delivery.description") },
                        ].map((s) => (
                            <div key={s.n} className="rounded-2xl bg-white/5 border border-white/10 p-7 backdrop-blur-sm hover:bg-white/10 transition">
                                <div className="text-emerald-400 font-bold text-sm tracking-widest">{s.n}</div>
                                <h3 className="mt-4 font-bold text-lg">{s.title}</h3>
                                <p className="mt-2 text-sm text-white/70 leading-relaxed">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* OEM & PRIVATE LABEL */}
            <section className="section-y">
                <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
                    <ScrollReveal>
                        <div className="overline mb-4">{t("home.oem.eyebrow")}</div>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0A2540] tracking-tight leading-tight">
                            {t("home.oem.title")}
                        </h2>
                        <p className="mt-5 text-slate-600 leading-relaxed text-lg">
                            {t("home.oem.description")}
                        </p>
                        <ul className="mt-8 space-y-3 text-slate-700">
                            {t("home.oem.features", { returnObjects: true }).map((b) => (
                                <li key={b} className="flex items-start gap-3">
                                    <ClipboardCheck className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" strokeWidth={2} />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-8">
                            <Link to="/custom-sourcing">
                                <Button className="rounded-full bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-semibold px-7 py-6 text-base">
                                    {t("home.oem.button")} <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                            </Link>
                        </div>
                    </ScrollReveal>
                    <ScrollReveal delay={80}>
                        <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-[#0A2540]/10">
                            <img src="https://images.unsplash.com/photo-1625471592808-3b848a6e9ffd?w=1400" alt="Private label textiles" className="w-full h-full object-cover" loading="lazy" />
                        </div>
                    </ScrollReveal>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="section-y bg-[#F4FBF8]">
                <div className="container-x">
                    <ScrollReveal>
                        <SectionHeading eyebrow={t("home.testimonials.eyebrow")} title={t("home.testimonials.title")} />
                    </ScrollReveal>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {testimonials.map((t, i) => (
                            <ScrollReveal key={i} delay={i * 60}>
                                <blockquote className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm h-full flex flex-col">
                                    <svg className="w-8 h-8 text-[#059669] mb-4" fill="currentColor" viewBox="0 0 32 32"><path d="M0 20h6L10 8H0v12zM16 20h6L26 8H16v12z" /></svg>
                                    <p className="text-slate-700 leading-relaxed flex-1">"{t.quote}"</p>
                                    <div className="mt-6 pt-6 border-t border-slate-100">
                                        <div className="font-bold text-[#0A2540]">{t.author}</div>
                                        <div className="text-sm text-slate-500">{t.company}</div>
                                    </div>
                                </blockquote>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CLIENT LOGO MARQUEE (placeholder) */}
            <section className="py-14 bg-white border-y border-slate-200 overflow-hidden">
                <div className="container-x">
                    <div className="text-center text-xs uppercase tracking-[0.25em] text-slate-400 mb-8">{t("home.marquee.text")}</div>
                    <div className="relative overflow-hidden">
                        <div className="flex gap-14 w-max animate-[marquee_24s_linear_infinite]">
                            {[...Array(2)].flatMap(() => ["Marriott", "Radisson", "Carrefour", "Lulu", "Emaar", "SPAR", "GEMS", "Aster DM", "Almarai", "Choithrams"]).map((n, i) => (
                                <div key={i} className="text-[#0A2540]/60 font-extrabold text-2xl whitespace-nowrap tracking-[0.2em]">{n}</div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* VIDEO EMBED */}
            <section className="section-y">
                <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
                    <ScrollReveal>
                        <div className="space-y-4">
                            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#1D4ED8]">Featured in Industry Conversations</div>
                                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                                    Our founder interviewed by a 15-year export industry veteran — an honest conversation about the real journey behind Exportfy.
                                </p>
                            </div>
                            <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl shadow-[#0A2540]/15 bg-[#0A2540]">
                                <iframe
                                    className="w-full h-full"
                                    src="https://www.youtube.com/embed/F7H391DxeN8"
                                    title="Exportfy — the story"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    loading="lazy"
                                />
                            </div>
                        </div>
                    </ScrollReveal>
                    <ScrollReveal delay={80}>
                        <div className="overline mb-4">{t("home.video.eyebrow")}</div>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0A2540] tracking-tight leading-tight">
                            {t("home.video.title")}
                        </h2>
                        <p className="mt-5 text-slate-600 leading-relaxed text-lg">
                            {t("home.video.description")}
                        </p>
                        <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-[#1D4ED8] font-semibold hover:gap-3 transition-all">
                            <Play className="w-4 h-4" /> {t("home.video.link")} <ArrowRight className="w-4 h-4" />
                        </Link>
                    </ScrollReveal>
                </div>
            </section>

            {/* EXPORTFY AI */}
            <section className="section-y bg-[#0B2F28] text-white relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none [background:radial-gradient(circle_at_20%_20%,rgba(16,185,129,0.15),transparent_40%),radial-gradient(circle_at_80%_60%,rgba(29,78,216,0.18),transparent_45%)]" />
                <div className="relative container-x">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-amber-300">
                            <Sparkles className="w-3.5 h-3.5" /> {t("home.ai.eyebrow")}
                        </div>
                        <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
                            {t("home.ai.title")}
                        </h2>
                        <p className="mt-5 text-white/70 leading-relaxed text-lg">
                            {t("home.ai.description")}
                        </p>
                        <Link to="/exportfy-ai" className="inline-flex mt-8">
                            <Button className="rounded-full bg-white text-[#0A2540] hover:bg-slate-100 font-semibold px-7 py-6">
                                {t("home.ai.button")} <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                        </Link>
                    </div>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        {["AI Buyer Finder", "AI Supplier Finder", "AI Quotation Generator", "AI HS Code Assistant", "AI Compliance Advisor"].map((f, i) => (
                            <div key={f} className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur p-6">
                                <div className="text-emerald-400 text-xs font-bold tracking-widest">FEATURE 0{i + 1}</div>
                                <div className="mt-3 font-bold text-lg">{f}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="section-y">
                <div className="container-x max-w-4xl">
                    <ScrollReveal>
                        <SectionHeading eyebrow={t("faq.eyebrow")} title={t("faq.title")} />
                    </ScrollReveal>
                    <Accordion type="single" collapsible className="mt-10 space-y-3">
                        {homeFAQs.map((f, i) => (
                            <AccordionItem key={i} value={`item-${i}`} className="border border-slate-200 rounded-2xl px-6 data-[state=open]:bg-slate-50 transition-colors">
                                <AccordionTrigger className="text-left font-bold text-[#0A2540] hover:no-underline">{f.q}</AccordionTrigger>
                                <AccordionContent className="text-slate-600 leading-relaxed pb-6">{f.a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="section-y bg-[#103C34] text-white">
                <div className="container-x">
                    <ScrollReveal>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                            <div>
                                <div className="overline text-emerald-400">{t("finalCta.eyebrow")}</div>
                                <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
                                    {t("finalCta.title")}
                                </h2>
                                <p className="mt-5 text-white/70 text-lg leading-relaxed">
                                    {t("finalCta.subtitle")}
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-3 lg:justify-end">
                                <Link to="/consultation">
                                    <Button className="rounded-full bg-white text-[#0A2540] hover:bg-slate-100 font-semibold px-7 py-6">
                                        {t("finalCta.cta1")} <ArrowRight className="ml-2 w-4 h-4" />
                                    </Button>
                                </Link>
                                <Link to="/custom-sourcing">
                                    <Button className="rounded-full bg-[#059669] hover:bg-[#047857] text-white font-semibold px-7 py-6">
                                        {t("finalCta.cta2")}
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </ScrollReveal>
                </div>
            </section>
        </>
    );
}
